import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@hooks/security/use-session';
import { useAssessHiv } from '@hooks/register/screening/use-assess-hiv';
import { useLocalize } from '@hooks/use-localize';
import { useStepForm } from '@hooks/use-step-form';
import { assessHivSchema } from '@views/register/screening/assess-hiv.schema';
import { asStringYesNoUnknown } from '@/utils/parse';
import { DashRegistrationStep, screeningLinkMap } from '@/types/dashboard';
import type { IAssessHivFormValues, IAssessHivModel, IAssessHivSubmitResult } from '@/types/screening/assess-hiv';
import PageLoader from '@components/page-loader';
import StepFormLayout from '@components/step-form-layout';
import FieldError from '@components/field-error';

const AssessHiv = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useAssessHiv(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <AssessHivContent model={model} isSubmitting={isSubmitting} submitError={submitError} onSubmit={submit} />
        </PageLoader>
    );
};

interface IAssessHivContentProps {
    model: IAssessHivModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IAssessHivFormValues) => Promise<IAssessHivSubmitResult>;
}

const AssessHivContent = ({ model, isSubmitting, submitError, onSubmit }: IAssessHivContentProps) => {
    const t = useLocalize(model.languageData);

    const form = useStepForm<IAssessHivFormValues, IAssessHivSubmitResult>({
        resolver: zodResolver(assessHivSchema),
        defaultValues: {
            hasHiv: asStringYesNoUnknown(model.hasHiv),
        },
        submit: onSubmit,
        linkMap: screeningLinkMap(model.isFemale, model.isLiver),
    });

    const {
        register,
        formState: { errors },
    } = form;

    return (
        <StepFormLayout
            kind="screening"
            model={model}
            step={DashRegistrationStep.AssessHiv}
            title={t('DashLabel_ProgressHIV', 'HIV')}
            t={t}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
        >
            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t('DashLabel_DoYouHaveAHistoryOfHiv', 'Have you ever been diagnosed with HIV')}
                    ?<FieldError message={errors.hasHiv?.message} />
                </label>
                <div className="col-xs-12" style={{ marginLeft: 10 }}>
                    <label className="radio-inline">
                        <input type="radio" value="1" {...register('hasHiv')} /> {t('Word_Yes', 'Yes')}
                    </label>
                    <label className="radio-inline">
                        <input type="radio" value="0" {...register('hasHiv')} /> {t('Word_No', 'No')}
                    </label>
                    <label className="radio-inline">
                        <input type="radio" value="-1" {...register('hasHiv')} /> {t('DashLabel_IDonTKnow', "I don't know")}
                    </label>
                </div>
            </div>
        </StepFormLayout>
    );
};

export default AssessHiv;
